/**
 * Live session view (player + strategist).
 *
 * Route: /live/:teamId/sessions/:sessionId/matches/:matchId
 * Strategist controls render only when team membership role is "strategist".
 *
 * Design refs:
 * - docs/prototypes/stratos-live-player.html
 * - docs/prototypes/stratos-live-strategist.html
 */
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useMyTeam } from '../../team/useMyTeam';
import { useIsStrategist } from '../hooks/useIsStrategist';
import { useLiveMatch } from '../hooks/useLiveMatch';
import { useNow } from '../hooks/useNow';
import { useStrategistActions } from '../hooks/useStrategistActions';
import { formatMapName, teamShortName } from '../lib/formatters';
import { buildLiveControlsPath } from '../lib/pickJoinTarget';
import { getPhaseRemainingSecs } from '../lib/roundClock';
import ActionStream from './ActionStream';
import LiveTopBar from './LiveTopBar';
import MatchControlsBar from './MatchControlsBar';
import NextStrategyPanel from './NextStrategyPanel';
import RolesPanel from './RolesPanel';
import RoundHistoryPanel from './RoundHistoryPanel';
import styles from './LivePlayerPage.module.css';

export default function LivePlayerPage() {
  const { teamId, sessionId, matchId } = useParams<{
    teamId: string;
    sessionId: string;
    matchId: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { team } = useMyTeam(user?.id);
  const isStrategist = useIsStrategist(teamId);
  const { join, liveState, roles, history, loading, error, refetch } = useLiveMatch({
    teamId,
    sessionId,
    matchId,
  });
  const now = useNow(500);

  const strategist = useStrategistActions({
    teamId: teamId ?? '',
    sessionId: sessionId ?? '',
    matchId: matchId ?? '',
    liveState,
    onSuccess: refetch,
  });

  const onExit = useCallback(() => {
    window.close();
    navigate('/dashboard');
  }, [navigate]);

  if (!teamId || !sessionId || !matchId) {
    return (
      <div className={styles.shell}>
        <Status
          title="Invalid live session link"
          body="This URL is missing a team, session, or match id."
          onExit={onExit}
        />
      </div>
    );
  }

  if (loading && !liveState) {
    return (
      <div className={styles.shell}>
        <Status title="Joining live session…" body="Connecting to your match." onExit={onExit} />
      </div>
    );
  }

  if (error && !liveState) {
    return (
      <div className={styles.shell}>
        <Status
          title="Could not join live session"
          body={error.message || 'Something went wrong.'}
          onExit={onExit}
          onRetry={refetch}
        />
      </div>
    );
  }

  if (!liveState) {
    return (
      <div className={styles.shell}>
        <Status title="No live data" body="Live state is not available yet." onExit={onExit} onRetry={refetch} />
      </div>
    );
  }

  const teamShort =
    team && team.id === teamId ? teamShortName(team.name) : teamShortName(team?.name ?? 'Team');
  const clockSecs = getPhaseRemainingSecs(liveState.currentRound, now);
  const waiting = !liveState.currentRound;
  const userName = user?.displayName || user?.email || 'Player';
  const badgeRole =
    liveState.myRole?.name ?? (isStrategist ? 'Strategist' : null);

  const currentHistory =
    liveState.currentRound
      ? {
          roundNumber: liveState.currentRound.roundNumber,
          strategyName: liveState.strategy?.name ?? '—',
          side: liveState.strategy?.side ?? '',
        }
      : null;

  const status = liveState.currentRound?.status;
  const showNextSlot =
    isStrategist &&
    liveState.currentRound != null &&
    (status === 'buy' || status === 'active');

  const nextHistory = showNextSlot
    ? {
        roundNumber:
          strategist.pendingRoundNumber ?? liveState.currentRound!.roundNumber + 1,
        strategyName: strategist.selectedStrategyName,
        side: strategist.selectedStrategySide,
      }
    : null;

  const nextStrategyLabel = strategist.selectedStrategyName;

  return (
    <div className={styles.shell}>
      <LiveTopBar
        teamShort={teamShort}
        wins={liveState.score.wins}
        losses={liveState.score.losses}
        roundNumber={liveState.currentRound?.roundNumber ?? null}
        mapLabel={formatMapName(liveState.map)}
        side={liveState.currentSide ?? null}
        userName={userName}
        roleName={badgeRole}
        onExit={onExit}
        secondaryAction={
          isStrategist
            ? {
                label: 'Controls only',
                onClick: () =>
                  navigate(
                    buildLiveControlsPath({
                      teamId,
                      sessionId,
                      matchId,
                      map: liveState.map,
                    }),
                  ),
              }
            : null
        }
      />

      <div className={styles.body}>
        <aside className={styles.side}>
          <NextStrategyPanel
            teamId={teamId}
            map={liveState.map}
            side={liveState.currentSide}
            isStrategist={isStrategist}
            selectedStrategyId={strategist.selectedStrategyId}
            selectedStrategyName={strategist.selectedStrategyName}
            liveStrategy={liveState.strategy}
            currentRoundNumber={liveState.currentRound?.roundNumber ?? null}
            busy={strategist.pendingAction != null}
            strategyNeedsConfirm={strategist.strategyNeedsConfirm}
            canConfirmStrategy={strategist.canConfirmStrategy}
            strategyConfirmed={
              strategist.selectedStrategyId != null &&
              strategist.selectedStrategyId === strategist.appliedStrategyId
            }
            onSelect={strategist.selectStrategyDraft}
            onConfirm={() => {
              void strategist.confirmStrategy();
            }}
          />
          <RolesPanel
            roundNumber={roles?.roundNumber ?? liveState.currentRound?.roundNumber ?? null}
            assignments={roles?.assignments ?? []}
            youPlayerId={join?.playerId}
            youPlayerName={userName}
          />
          <RoundHistoryPanel
            completed={history?.rounds ?? []}
            current={currentHistory}
            next={nextHistory}
          />
        </aside>

        <main className={styles.main}>
          {isStrategist && (
            <MatchControlsBar
              round={liveState.currentRound}
              clockSecs={clockSecs}
              nextStrategyLabel={nextStrategyLabel}
              startingSide={liveState.startingSide}
              currentSide={liveState.currentSide}
              pendingAction={strategist.pendingAction}
              actionError={strategist.actionError}
              onStart={() => void strategist.start()}
              onWin={() => void strategist.endWin()}
              onLoss={() => void strategist.endLoss()}
              onReset={() => void strategist.resetClock()}
              onSetClock={(secs) => void strategist.setClock(secs)}
              onPlant={() => void strategist.plant()}
              onDefuse={() => void strategist.defuse()}
              onNextRound={() => void strategist.nextRound()}
              onEndGame={() => {
                void strategist.endMatch().then((result) => {
                  if (result) navigate('/live', { state: { endedMatch: result } });
                });
              }}
              onSetStartingSide={(side) => void strategist.setStartingSide(side)}
            />
          )}
          <div className={styles.streamArea}>
            <ActionStream
              actions={liveState.myActions}
              currentClockSecs={clockSecs}
              strategyName={liveState.strategy?.name}
              side={liveState.strategy?.side}
              buyType={liveState.strategy?.buyType}
              waiting={waiting}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

function Status({
  title,
  body,
  onExit,
  onRetry,
}: {
  title: string;
  body: string;
  onExit: () => void;
  onRetry?: () => void;
}) {
  return (
    <div className={styles.status}>
      <div className={styles.statusCard}>
        <h1 className={styles.statusTitle}>{title}</h1>
        <p className={styles.statusBody}>{body}</p>
        <div className={styles.statusActions}>
          {onRetry && (
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onRetry}>
              Retry
            </button>
          )}
          <button type="button" className={styles.btn} onClick={onExit}>
            Exit
          </button>
        </div>
      </div>
    </div>
  );
}
